/**
 * Spacing Guidelines in React Native
 *
 * 1️⃣ Padding
 * - Use for internal space between a component's content and its border/background.
 * - Defined on the component itself.
 * - Common uses:
 *    • Prevent content from touching edges
 *    • Increase touchable area
 * - Do NOT use padding for spacing between separate components.
 *
 * 2️⃣ Margin
 * - Use for external space between a component and other components.
 * - Ideally controlled by the parent layout, not the component itself.
 * - Common uses:
 *    • Positioning within a layout
 *    • Separation from surrounding elements
 * - Do NOT use margin for consistent spacing between siblings when `gap` is available.
 *
 * 3️⃣ Gap (React Native 0.71+)
 * - Use for even spacing between sibling elements in a Flexbox or Grid container.
 * - Defined on the parent container, not children.
 * - Eliminates the need for per-child margins for spacing.
 *
 * 🔑 Golden Rule:
 * - Padding → Internal breathing room (component responsibility)
 * - Margin  → External separation (layout responsibility)
 * - Gap     → Equal sibling spacing (parent responsibility)
 */

// Layout constants for responsive design
export const layout = {
  // Maximum width for desktop to prevent overly wide layouts
  maxWidth: 768, // Tablet/desktop max width
  // Minimum width for desktop to ensure usability
  minWidth: 320, // Minimum width for desktop (standard mobile width)

  // Simplified unified spacing system (4px base unit)
  space: {
    xs: 4, // Micro spacing (icon padding, fine adjustments)
    sm: 8, // Small spacing (margins, small gaps)
    md: 12, // Medium spacing (standard gaps, padding)
    lg: 16, // Large spacing (component padding, section spacing)
    xl: 20 // Extra large spacing (major sections)
  },

  // Standardized border radius values
  borderRadius: {
    small: 4, // Small elements (checkboxes, small buttons)
    medium: 8, // Standard elements (forms, drawer items, modals)
    large: 12, // Cards, buttons, containers
    round: 20 // Pill-shaped elements (tags, chat messages)
  },

  // Standardized border width values
  borderWidth: {
    thin: 1, // Standard borders for most components
    thick: 2 // Emphasized borders (checkboxes, active states)
  },

  // Standardized icon size system (4px base unit, aligned with spacing)
  size: {
    xs: 16, // Extra small icons (inline with small text, section headers)
    sm: 18, // Small icons (checkboxes, compact buttons)
    md: 20, // Medium icons (standard default size, most common)
    lg: 24, // Large icons (buttons, prominent actions)
    xl: 40 // Extra large icons (play buttons, prominent controls)
  },

  // Modal card width presets. Sized below `maxWidth` so a modal never feels
  // like a full-screen takeover on desktop.
  // - `dialog` is small confirmations.
  // - `medium` is short multi-section content (resources, summaries).
  // - `wide` is forms that need breathing room for inputs (feedback).
  modal: {
    dialog: 360,
    medium: 520,
    wide: 640
  }
} as const;
